/**
 * Contrato e chamadas do console interno — o que o consultor faz numa implantação (F7).
 *
 * **Por que este módulo existe, e não mora em `implantacoes.ts` nem em `kickoff.ts`.**
 * `implantacoes.ts` é a esteira que as DUAS áreas leem: o painel do cliente e a carteira. E o
 * cabeçalho de `kickoff.ts` declara, com todas as letras, que nenhuma chamada dele recebe id de
 * implantação — é essa ausência de parâmetro que torna impossível, do lado do navegador, pedir a
 * trilha de outra empresa. Os atos daqui são o contrário disso: todos recebem o id, todos exigem
 * lado interno e permissão própria, e nenhum deles tem sentido na área do cliente. Misturá-los
 * com o motor de formulário apagaria a diferença que protege o cliente.
 *
 * Três regras moram aqui:
 *
 * · **A tela não recalcula estado nenhum.** Etapa, vez, prazo e pendência chegam derivados da
 *   linha do tempo, na MESMA rota que alimenta o painel do cliente (`/esteira` contra `/minha`).
 *   É o que impede o console de dizer "aguardando infra" enquanto o cliente lê "concluído".
 *
 * · **`contrato:ver` é opcional, e por isso o detalhe do cadastro também é.** A matriz da spec §4
 *   não dá essa permissão à infraestrutura, que é justamente quem libera o ambiente. Se o detalhe
 *   fosse obrigatório, a tela inteira falharia para quem tem exatamente a permissão do ato
 *   principal dela. `403` vira `null` — a tela mostra o que a esteira já garante e diz o que não
 *   pôde ler.
 *
 * · **Liberar o ambiente registra um dos dois marcos do prazo.** A rota é própria
 *   (`POST /ambiente-liberado`) porque o ato exige `ambiente:liberar`. Como `tipo` na rota
 *   genérica, quem tem `implantacao:editar` poderia registrar o marco por um cliente.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ErroDaApi, atualizarJson, enviarFormulario, enviarJson, pedirJson } from "./api";
import {
  CHAVE_LISTAGEM,
  CHAVE_MINHA_ESTEIRA,
  esquemaContrato,
  esquemaEsteira,
  esquemaImplantacao,
  type Esteira,
} from "./implantacoes";
import { CHAVE_RAIZ_DO_KICKOFF, chaveDaRevisao } from "./kickoff";
import type { SecaoDaImplantacao } from "./secoesDaImplantacao";

/* ----------------------------------------------------------------------- permissões */

/* Espelham a matriz da spec §4, e servem para a tela **não oferecer** o que o servidor vai
   negar — nunca para proteger. Quem nega é o serviço, que tem a sessão de banco e a auditoria;
   o ADR 0006 é literal a respeito: *"tela é encaminhamento"*. */
export const PERMISSAO_DE_VER_CONTRATO = "contrato:ver";
export const PERMISSAO_DE_LIBERAR_AMBIENTE = "ambiente:liberar";
/* Definir o consultor é editar a implantação — mesma permissão de quem cadastra e corrige
   dados, e não a de liberar ambiente, que registra um marco do prazo. */
export const PERMISSAO_DE_EDITAR_IMPLANTACAO = "implantacao:editar";
export const PERMISSAO_DE_REVISAR_KICKOFF = "kickoff:revisar";
/* A listagem do cofre é servida com `infra:ver` — a mesma permissão da trilha de
   infraestrutura, e não uma permissão de cofre própria. */
export const PERMISSAO_DE_VER_COFRE = "infra:ver";
/** As respostas do questionário de infraestrutura — a mesma permissão, pelo mesmo motivo. */
export const PERMISSAO_DE_VER_INFRA = "infra:ver";
export const PERMISSAO_DE_GERAR_PACOTE = "pacote:gerar";

/* --------------------------------------------------------------------------- caminhos */

/** A tela de uma implantação no console. Um lugar só, para as outras telas apontarem. A seção
 *  vai no hash (SUS-15400): quem volta do pacote, da revisão ou da agenda cai no card de onde
 *  saiu, e não em Contatos de acesso. */
export function caminhoDaImplantacao(implantacaoId: string, secao?: SecaoDaImplantacao): string {
  return `/admin/implantacoes/${encodeURIComponent(implantacaoId)}${secao ? `#${secao}` : ""}`;
}

/** A tela do pacote de Kick Off de uma implantação. */
export function caminhoDoPacote(implantacaoId: string): string {
  return `/admin/implantacoes/${encodeURIComponent(implantacaoId)}/pacote`;
}

/* ---------------------------------------------------------------------------- esquemas */

const esquemaPessoa = z.object({
  id: z.string().min(1),
  nome: z.string().min(1),
  email: z.string().min(1),
});

/**
 * A implantação como o servidor a devolve depois da liberação do ambiente.
 *
 * Os três campos entram como **opcionais** de propósito. `esquemaImplantacao` é o mesmo contrato
 * que a resposta do cadastro usa, e o zod recusa o objeto inteiro quando falta uma chave
 * obrigatória: exigir aqui o que o cadastro não precisa ler transformaria um dado a mais numa
 * tela quebrada.
 */
export const esquemaDetalheDaImplantacao = esquemaImplantacao.extend({
  consultor: esquemaPessoa.nullable().optional(),
  criada_em: z.string().optional(),
});

export type DetalheDaImplantacao = z.infer<typeof esquemaDetalheDaImplantacao>;

const esquemaEvento = z.object({
  id: z.string().min(1),
  /* Texto, e não `z.enum`: o vocabulário é fechado no BANCO (tipo enumerado), e acrescentar um
     evento numa fatia futura é `ALTER TYPE ... ADD VALUE`. Se esta camada repetisse a lista, o
     primeiro evento novo derrubaria a linha do tempo inteira em vez de aparecer sem rótulo
     bonito — que é o pior jeito possível de descobrir que houve uma migration. */
  tipo: z.string().min(1),
  ocorrido_em: z.string().min(1),
  ator: z.string(),
  detalhe: z.record(z.string(), z.unknown()),
  pendencia: z
    .object({
      pergunta: z.string().nullable(),
      respondida_em: z.string().nullable(),
    })
    .nullable()
    .optional(),
});

const esquemaLinhaDoTempo = z.object({
  implantacao_id: z.string().min(1),
  eventos: z.array(esquemaEvento),
});

export type EventoDaImplantacao = z.infer<typeof esquemaEvento>;
export type LinhaDoTempo = z.infer<typeof esquemaLinhaDoTempo>;

const esquemaImplantacaoAguardandoLiberacao = z.object({
  id: z.string().min(1),
  organizacao: z.string().min(1),
  respondida_em: z.string().nullable(),
});

export type ImplantacaoAguardandoLiberacao = z.infer<
  typeof esquemaImplantacaoAguardandoLiberacao
>;

const esquemaPendenciaApontada = z.object({
  implantacao_id: z.string().min(1),
  referencia: z.string().min(1),
  trilha: z.string().min(1),
  pergunta_id: z.string().nullable(),
  descricao: z.string().min(1),
  /* Falso quando a trilha ainda não tinha sido enviada: não havia congelamento a desfazer. A
     pendência vale do mesmo jeito, e a tela precisa da diferença para não prometer ao consultor
     uma devolução que não aconteceu. */
  trilha_devolvida: z.boolean(),
  estado_da_trilha: z.string().min(1),
});

const esquemaAceiteDoKickoff = z.object({
  implantacao_id: z.string().min(1),
  aceito_em: z.string().min(1),
  aceito_por: z.string(),
  trilhas_enviadas: z.array(z.string()),
  /* Não é erro: é o registro de que o consultor aceitou sabendo o que faltava. Sem isto,
     "kickoff aceito" seria uma frase sem escopo. */
  trilhas_pendentes: z.array(z.string()),
  contradicoes: z.number(),
  pendencias_resolvidas: z.array(z.string()),
});

/* As respostas da infraestrutura ao lado da liberação do ambiente (spec de 28/09, 6.3). Vêm por
   `GET /implantacoes/{id}/infraestrutura`, com `infra:ver` — quem libera é a infraestrutura, que
   não revisa kickoff, então a revisão não servia. O texto já vem legível do servidor, e a
   credencial vem só como "está no cofre": nem valor, nem referência. */
const esquemaRespostaDaInfraestrutura = z.object({
  pergunta_id: z.string().min(1),
  enunciado: z.string().min(1),
  credencial: z.boolean(),
  guardada: z.boolean(),
  linhas: z.array(z.string()),
});

const esquemaQuestionarioDeInfraestrutura = z.object({
  implantacao_id: z.string().min(1),
  disponivel: z.boolean(),
  nome: z.string().nullable(),
  estado: z.enum(["nao_iniciada", "em_preenchimento", "enviada"]),
  enviada_em: z.string().nullable(),
  progresso: z
    .object({
      perguntas_visiveis: z.number(),
      respondidas: z.number(),
      obrigatorias_visiveis: z.number(),
      obrigatorias_respondidas: z.number(),
      percentual: z.number(),
      concluida: z.boolean(),
    })
    .nullable(),
  respostas: z.array(esquemaRespostaDaInfraestrutura),
});

export type RespostaDaInfraestrutura = z.infer<typeof esquemaRespostaDaInfraestrutura>;
export type QuestionarioDeInfraestrutura = z.infer<typeof esquemaQuestionarioDeInfraestrutura>;

export type PendenciaApontada = z.infer<typeof esquemaPendenciaApontada>;
export type AceiteDoKickoff = z.infer<typeof esquemaAceiteDoKickoff>;

/* ---------------------------------------------------------------------------- consultas */

export const CHAVE_RAIZ_DO_CONSOLE = ["console"] as const;

export function chaveDaEsteira(implantacaoId: string) {
  return ["console", "esteira", implantacaoId] as const;
}

export function chaveDoDetalhe(implantacaoId: string) {
  return ["console", "detalhe", implantacaoId] as const;
}

export function chaveDaLinhaDoTempo(implantacaoId: string) {
  return ["console", "eventos", implantacaoId] as const;
}

function base(implantacaoId: string): string {
  return `/implantacoes/${encodeURIComponent(implantacaoId)}`;
}

/** A esteira de UMA implantação — a mesma derivação que o cliente lê em `/minha`. */
export function useEsteiraDaImplantacao(implantacaoId: string) {
  return useQuery<Esteira>({
    queryKey: chaveDaEsteira(implantacaoId),
    queryFn: ({ signal }) => pedirJson(`${base(implantacaoId)}/esteira`, esquemaEsteira, { signal }),
    enabled: implantacaoId.length > 0,
    retry: false,
  });
}

/**
 * O detalhe do cadastro, ou `null` quando a sessão não tem `contrato:ver`.
 *
 * `403` **não é falha**: é a matriz da spec §4 funcionando. A infraestrutura libera o ambiente e
 * não lê o contrato; se a negativa virasse erro, a tela inteira quebraria para quem tem
 * exatamente a permissão do ato principal dela.
 */
export function useDetalheDaImplantacao(implantacaoId: string, habilitada = true) {
  return useQuery<DetalheDaImplantacao | null>({
    queryKey: chaveDoDetalhe(implantacaoId),
    queryFn: async ({ signal }) => {
      try {
        return await pedirJson(base(implantacaoId), esquemaDetalheDaImplantacao, { signal });
      } catch (causa) {
        if (causa instanceof ErroDaApi && causa.status === 403) return null;
        throw causa;
      }
    },
    enabled: habilitada && implantacaoId.length > 0,
    retry: false,
  });
}

/** A linha do tempo crua — a fonte da esteira, sem derivação nenhuma no meio. */
export function useLinhaDoTempo(implantacaoId: string) {
  return useQuery<LinhaDoTempo>({
    queryKey: chaveDaLinhaDoTempo(implantacaoId),
    queryFn: ({ signal }) =>
      pedirJson(`${base(implantacaoId)}/eventos`, esquemaLinhaDoTempo, { signal }),
    enabled: implantacaoId.length > 0,
    retry: false,
  });
}

/** A fila viva da infraestrutura: questionário enviado e ambiente ainda não liberado. */
export function useImplantacoesAguardandoLiberacao(habilitada = true) {
  return useQuery<ImplantacaoAguardandoLiberacao[]>({
    queryKey: ["console", "aguardando-liberacao"],
    queryFn: ({ signal }) =>
      pedirJson(
        "/implantacoes/aguardando-liberacao",
        z.array(esquemaImplantacaoAguardandoLiberacao),
        { signal },
      ),
    enabled: habilitada,
    retry: false,
  });
}

/** As respostas da infraestrutura, para quem tem `infra:ver`. Desligada sem a permissão. */
export function useQuestionarioDeInfraestrutura(implantacaoId: string, habilitada = true) {
  return useQuery<QuestionarioDeInfraestrutura>({
    queryKey: ["console", "infraestrutura", implantacaoId],
    queryFn: ({ signal }) =>
      pedirJson(
        `${base(implantacaoId)}/infraestrutura`,
        esquemaQuestionarioDeInfraestrutura,
        { signal },
      ),
    enabled: habilitada && implantacaoId.length > 0,
    retry: false,
  });
}

/* ----------------------------------------------------------------------------- escritas */

export interface PedidoDeLiberacao {
  /** `null` deixa o servidor datar em hoje. Data no futuro é recusada por ele. */
  liberado_em: string | null;
  observacao: string | null;
}

/**
 * Confirma que o ambiente está liberado — o prazo espera também o aceite do kickoff.
 *
 * A invalidação é ampla de propósito: a liberação muda a `entrega_prevista` (coluna gerada no
 * banco), a etapa derivada, a linha do tempo e a linha da carteira. Invalidar só o detalhe
 * deixaria a esteira ao lado dizendo, na mesma tela, que o prazo ainda não começou.
 */
export function useLiberarAmbiente(implantacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeLiberacao) =>
      enviarJson(
        `${base(implantacaoId)}/ambiente-liberado`,
        esquemaDetalheDaImplantacao,
        corpo,
      ),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_CONSOLE });
      void cliente.invalidateQueries({ queryKey: CHAVE_LISTAGEM });
    },
  });
}

/**
 * Troca o consultor da conta (o responsável pela venda; campo único desde a 0105). O servidor
 * recusa `null`: a conta não fica sem consultor.
 *
 * A invalidação é ampla pelo mesmo motivo da liberação de ambiente, com uma consequência a mais:
 * **o contato do CLIENTE muda junto**. Quem ele procura passa a ser o novo consultor — e a
 * esteira dele lê o mesmo campo derivado que o console.
 */
export function useDefinirConsultor(implantacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (consultorId: string | null) =>
      atualizarJson(`${base(implantacaoId)}/consultor`, esquemaDetalheDaImplantacao, {
        consultor_id: consultorId,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_CONSOLE });
      void cliente.invalidateQueries({ queryKey: CHAVE_LISTAGEM });
      void cliente.invalidateQueries({ queryKey: CHAVE_MINHA_ESTEIRA });
    },
  });
}

/**
 * Anexa ou substitui o contrato assinado (PDF) — equipe interna. É o caminho de regularização
 * das implantações abertas antes da regra, que não têm contrato.
 */
export function useAnexarContrato(implantacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (arquivo: File) => {
      const formulario = new FormData();
      formulario.append("contrato", arquivo);
      return enviarFormulario(`${base(implantacaoId)}/contrato`, esquemaContrato, formulario, "PUT");
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_CONSOLE });
      void cliente.invalidateQueries({ queryKey: CHAVE_MINHA_ESTEIRA });
    },
  });
}

export interface PedidoDePendencia {
  trilha: string;
  descricao: string;
  /** A pergunta exata, quando a pendência é sobre uma. `null` aponta a trilha inteira. */
  pergunta_id: string | null;
}

/**
 * Aponta a pendência ao cliente **e** devolve a trilha — os dois atos são um só.
 *
 * Não existe "avisar sem reabrir": a trilha congela no envio, e um aviso que não desfaz o
 * congelamento pede ao cliente uma correção que ele não consegue fazer. Era exatamente o beco de
 * antes da F7 — o consultor via a contradição na tela e o conserto continuava sendo telefonema.
 */
export function useApontarPendencia(implantacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDePendencia) =>
      enviarJson(
        `${base(implantacaoId)}/kickoff/pendencias`,
        esquemaPendenciaApontada,
        corpo,
      ),
    onSuccess: () => {
      /* A revisão muda (a trilha volta a `em_preenchimento`) e a esteira muda (a pendência
         aparece, e o kickoff deixa de constar como enviado). Duas telas, uma invalidação cada. */
      void cliente.invalidateQueries({ queryKey: chaveDaRevisao(implantacaoId) });
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_CONSOLE });
      void cliente.invalidateQueries({ queryKey: CHAVE_LISTAGEM });
    },
  });
}

export interface PedidoDeAceite {
  observacao: string | null;
}

/** Aceita as respostas do kickoff, registrando na esteira o que faltava no momento do aceite. */
export function useAceitarKickoff(implantacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeAceite) =>
      enviarJson(`${base(implantacaoId)}/kickoff/aceite`, esquemaAceiteDoKickoff, corpo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_KICKOFF });
      void cliente.invalidateQueries({ queryKey: CHAVE_RAIZ_DO_CONSOLE });
      void cliente.invalidateQueries({ queryKey: CHAVE_LISTAGEM });
    },
  });
}

/* ------------------------------------------------------------------- rótulos e formatação */

/**
 * O rótulo de cada tipo de evento da linha do tempo.
 *
 * `rotuloDoEvento` cai no próprio `tipo` quando não conhece o valor. É o que permite uma
 * migration acrescentar um evento sem que a linha do tempo apareça vazia até alguém lembrar de
 * mexer aqui — o dado continua legível, só perde o nome bonito.
 */
export const ROTULO_DO_EVENTO: Record<string, string> = {
  implantacao_criada: "Implantação aberta",
  contato_convidado: "Convite de acesso enviado",
  infra_iniciada: "Infraestrutura iniciada",
  infra_respondida: "Infraestrutura respondida",
  infra_devolvida: "Infraestrutura devolvida ao cliente",
  ambiente_liberado: "Ambiente liberado — aguardando geração da tarefa de kickoff",
  /* Sem "welcome": a reunião deixou de ser etapa e deixou de ser só a de abertura. O tipo
     (kickoff, acompanhamento, treinamento, encerramento) mora na própria reunião. */
  reuniao_agendada: "Reunião de implantação registrada",
  reuniao_realizada: "Reunião de implantação realizada",
  ata_publicada: "Ata publicada",
  kickoff_iniciado: "Kickoff iniciado",
  kickoff_enviado: "Respostas do kickoff enviadas",
  execucao_iniciada: "Execução iniciada",
  implantacao_concluida: "Implantação concluída",
  pendencia_aberta: "Pendência apontada ao cliente",
  pendencia_resolvida: "Pendência resolvida",
  observacao: "Observação registrada",
};

export function rotuloDoEvento(tipo: string, detalhe?: Record<string, unknown>): string {
  if (tipo === "contato_convidado" && detalhe?.convite === "nao_enviado") {
    return "Usuário vinculado à implantação";
  }
  return ROTULO_DO_EVENTO[tipo] ?? tipo;
}

/* O ambiente e a situação vêm do banco como chave. Os rótulos existem também no `/catalogo`,
   mas essa rota exige `implantacao:criar` — permissão do comercial, que o consultor e a
   infraestrutura não têm. Pedi-la só para escrever uma legenda faria a tela falhar por causa do
   rótulo, e não do dado. Chave desconhecida cai nela mesma, pelo motivo de `rotuloDoEvento`. */
const ROTULO_DO_AMBIENTE: Record<string, string> = {
  cliente: "Servidor próprio do cliente",
  cloud: "Cloud GEOvendas (nós provisionamos)",
  indefinido: "Ainda não definido em contrato",
};

const ROTULO_DA_SITUACAO: Record<string, string> = {
  novo: "Cliente novo",
  contratando_modulos: "Cliente atual — contratando novos módulos",
};

const ROTULO_DO_ESTADO: Record<string, string> = {
  ativa: "Ativa",
  concluida: "Concluída",
  cancelada: "Cancelada",
};

export function rotuloDoAmbiente(chave: string): string {
  return ROTULO_DO_AMBIENTE[chave] ?? chave;
}

export function rotuloDaSituacao(chave: string): string {
  return ROTULO_DA_SITUACAO[chave] ?? chave;
}

export function rotuloDoEstado(chave: string): string {
  return ROTULO_DO_ESTADO[chave] ?? chave;
}

/* `en-CA` produz `AAAA-MM-DD`, que é o formato que o `<input type="date">` e a API esperam. O
   fuso é fixo em São Paulo pelo mesmo motivo do resto do portal: às 22h de Brasília o navegador
   em UTC já está em outro dia, e a liberação seria datada amanhã — data no futuro, que o
   servidor recusa. */
const FORMATO_ISO_EM_BRASILIA = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  timeZone: "America/Sao_Paulo",
});

export function hojeEmBrasilia(): string {
  return FORMATO_ISO_EM_BRASILIA.format(new Date());
}

/**
 * A recusa de um ato do console em uma frase.
 *
 * As mensagens do serviço são escritas para quem está do outro lado da tela — "este ambiente já
 * foi liberado em 12/08/2026" —, então passam inteiras. A frase genérica só entra quando não veio
 * envelope nenhum: queda de rede, `500`.
 */
export function mensagemDoConsole(causa: unknown): string {
  if (causa instanceof ErroDaApi) {
    if (causa.status === null) {
      return "Não foi possível falar com o portal. Verifique a conexão e tente de novo.";
    }
    return causa.message;
  }
  return "Não foi possível concluir a operação agora. Tente de novo em instantes.";
}
